import React, { type FC, type SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import useStartWatchingButton, { type StartWatchingIconName } from '@jwp/ott-hooks-react/src/useStartWatchingButton';
import Play from '@jwp/ott-theme/assets/icons/play.svg?react';
import Scheduled from '@jwp/ott-theme/assets/icons/scheduled.svg?react';
import { testId as testIdUtil } from '@jwp/ott-common/src/utils/common';
import type { EpgChannel } from '@jwp/ott-common/types/epg';

import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';

import styles from './StartWatchingButton.module.scss';

type Props = {
  item: PlaylistItem;
  channel?: PlaylistItem | EpgChannel;
  playUrl?: string;
  disabled?: boolean;
  episodeMetadata?: { seasonNumber: string; episodeNumber: string };
  isSequentialEpisode?: boolean;
  onClick?: () => void;
};

const ICONS: Record<StartWatchingIconName, FC<SVGProps<SVGSVGElement>>> = {
  play: Play,
  scheduled: Scheduled,
};

const StartWatchingButton: React.FC<Props> = ({ item, playUrl, disabled = false, episodeMetadata, isSequentialEpisode, onClick, channel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const breakpoint = useBreakpoint();

  const { hasAccess, label, iconName, action, progress, testId } = useStartWatchingButton({
    item,
    channel,
    episodeMetadata,
    isSequentialEpisode,
  });

  const handleStartWatchingClick = () => {
    switch (action) {
      case 'play':
        if (onClick) {
          onClick();
          return;
        }
        return playUrl && navigate(playUrl);
      case 'authenticate':
        return navigate(modalURLFromLocation(location, 'create-account', { returnUrl: playUrl }));
      case 'choose-offer':
        // @TODO we should decide when to send the user to choose-offer or the payments page
        // but perhaps this isn't a scenario anymore...
        return navigate(modalURLFromLocation(location, 'choose-offer', { returnUrl: playUrl }));
      case 'none':
        return;
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <Button
      color="primary"
      variant="contained"
      size="large"
      label={label}
      startIcon={iconName ? <Icon icon={ICONS[iconName]} /> : undefined}
      onClick={handleStartWatchingClick}
      fullWidth={breakpoint < Breakpoint.md}
      disabled={disabled}
      data-testid={testId}
      data-mediaid={item.mediaid}
      aria-label={testIdUtil('start-watching')}
      className={progress ? styles.hasProgress : undefined}
    >
      {progress ? (
        <div className={styles.progressRail}>
          <div className={styles.progress} style={{ width: `${progress * 100}%` }} />
        </div>
      ) : null}
    </Button>
  );
};

export default StartWatchingButton;
